import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bell, Check, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/lib/supabaseClient';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
}

export default function Notifications() {
  const { user } = useUserRole();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from('notifications')
      .select('id, type, title, message, read, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(100);
    setItems((data as Notification[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (id: string) => {
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const remove = async (id: string) => {
    await supabase.from('notifications').delete().eq('id', id);
    setItems((prev) => prev.filter((n) => n.id !== id));
  };

  const markAllRead = async () => {
    if (!user) return;
    await supabase.from('notifications').update({ read: true }).eq('user_id', user.id).eq('read', false);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <div className="min-h-screen bg-surface-subtle p-6 flex justify-center">
      <div className="w-full max-w-2xl space-y-4">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-muted hover:text-ink">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <Card>
          <CardHeader
            title="Notifications"
            subtitle={`${items.filter((n) => !n.read).length} unread`}
            icon={<Bell className="w-4 h-4" />}
            action={
              <Button variant="secondary" size="sm" onClick={markAllRead}>
                Mark all read
              </Button>
            }
          />
          <CardBody className="space-y-2">
            {loading ? (
              <Spinner />
            ) : items.length === 0 ? (
              <EmptyState title="No notifications" icon={<Bell className="w-8 h-8" />} />
            ) : (
              items.map((n) => (
                <div key={n.id} className={`flex items-start gap-3 p-3 rounded-lg border ${n.read ? 'border-border-subtle' : 'border-primary-200 bg-primary-50'}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[13px] font-semibold text-ink truncate">{n.title}</p>
                      {!n.read && <Badge tone="primary">New</Badge>}
                    </div>
                    <p className="text-[12.5px] text-ink-muted">{n.message}</p>
                    <p className="text-[10.5px] text-ink-disabled mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {!n.read && (
                      <button onClick={() => markRead(n.id)} aria-label="Mark read" className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => remove(n.id)} aria-label="Delete" className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-error-bg text-error cursor-pointer">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
